import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, PenTool, Hammer, Wrench } from 'lucide-react';
import { servicesData } from '../data/services';
import SEO from '../components/SEO';

const ServiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = servicesData.find(s => s.id === parseInt(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!service) {
    return (
      <div className="pt-40 pb-24 min-h-screen flex flex-col items-center justify-center text-center bg-brand-light">
        <h1 className="text-4xl font-bold text-brand-dark mb-4">Service Not Found</h1>
        <button onClick={() => navigate('/services')} className="px-6 py-3 bg-brand-blue text-white rounded-full">
          Back to Services
        </button>
      </div>
    );
  }

  return (
    <>
      {/* 2. Place the SEO component at the very top of your page */}
      <SEO
        title={service.meta_title}
        description={service.meta_description}
        keywords={service.meta_keywords}
      />
      <div className="min-h-screen bg-brand-light pb-24">

        {/* Massive Hero Section */}
        <div className="relative w-full h-[60vh] min-h-[400px]">
          <div className="absolute inset-0 bg-brand-dark/40 z-10"></div>
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 z-20 flex flex-col justify-end pb-20 container mx-auto px-6 md:px-12">
            <Link to="/services" className="inline-flex items-center text-brand-cyan font-bold hover:text-white transition-colors mb-6 w-max bg-brand-dark/50 px-4 py-2 rounded-full backdrop-blur-sm">
              <ArrowLeft size={18} className="mr-2" /> Back to All Services
            </Link>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-7xl font-heading font-black text-white drop-shadow-2xl max-w-4xl"
            >
              {service.title}
            </motion.h1>
          </div>
        </div>

        <div className="container mx-auto px-6 md:px-12 max-w-6xl -mt-10 relative z-30">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col lg:flex-row">

            {/* Main Content Area */}
            <div className="lg:w-2/3 p-10 md:p-16">
              <h2 className="text-3xl font-heading font-bold text-brand-dark mb-8 border-b-4 border-brand-cyan pb-4 inline-block">
                Service Overview
              </h2>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                {service.paragraphs.map((paragraph, index) => (
                  <div key={index} className="flex items-start gap-4 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                    <CheckCircle className="text-brand-cyan flex-shrink-0 mt-1" size={28} />
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {paragraph}
                    </p>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Sticky Sidebar */}
            <div className="lg:w-1/3 bg-brand-dark p-10 md:p-16 text-white flex flex-col justify-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-cyan opacity-20 rounded-full blur-[80px] -mr-20 -mt-20"></div>

              <div className="relative z-10">
                <h3 className="text-2xl font-heading font-bold mb-4">Need {service.title}?</h3>
                <p className="text-gray-300 mb-8">
                  Our experts in Upland, CA are ready to design, fabricate, and install your custom signage to perfection.
                </p>
                <Link to="/contact" className="flex items-center justify-center w-full py-4 bg-brand-cyan text-brand-dark font-black text-lg rounded-full hover:bg-white transition-all duration-300 shadow-[0_0_20px_rgba(0,198,255,0.4)]">
                  Get a Free Quote
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* The 3-Step Process Section */}
        <div className="container mx-auto px-6 md:px-12 max-w-6xl mt-24 text-center">
          <h2 className="text-4xl font-heading font-bold text-brand-dark mb-16">Our Proven <span className="text-brand-blue">Process</span></h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { icon: <PenTool size={40} />, title: "1. Consultation & Design", desc: "We evaluate your needs, survey the site, and create custom digital mockups." },
              { icon: <Hammer size={40} />, title: "2. Custom Fabrication", desc: "Using premium materials, we manufacture your signage in our cutting-edge facility." },
              { icon: <Wrench size={40} />, title: "3. Expert Installation", desc: "Our licensed technicians ensure a flawless, safe, and code-compliant installation." }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
                className="bg-white p-10 rounded-3xl shadow-lg border-t-4 border-brand-cyan hover:-translate-y-2 transition-transform duration-300"
              >
                <div className="w-20 h-20 bg-brand-light text-brand-blue rounded-2xl flex items-center justify-center mx-auto mb-6">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-3">{step.title}</h3>
                <p className="text-gray-500">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </>
  );
};

export default ServiceDetailPage;